import { OpenHours } from "@phn/entities/dealer-response";
import { OpeningHours } from "@phn/entities/content";
import { isString } from "@phn/utility/helper";
import { navContentStore } from "@phn/state/nav-content-store";
import { timeformat } from "@phn/utility/constants/app";

export function showDealerSearchDropdown(country: string): boolean {
  const countriesWithDropdown = ["CN"];

  return countriesWithDropdown.includes(country);
}

export function getRegionSpecificCountry(country: string): string {
  return country?.includes("-") ? country.split("-")[0] : country;
}

export function getUsedUnit(country: string): "km" | "mi" {
  return ["US", "GB", "PR"].includes(getRegionSpecificCountry(country))
    ? "mi"
    : "km";
}

export function sanitizeDealerHomepage(originalUrl: string): string {
  try {
    // eslint-disable-next-line no-new
    new URL(originalUrl);
    return originalUrl;
  } catch (_) {
    return `http://${originalUrl}`;
  }
}

export function getArrangedWeekdays(openingHours: OpeningHours): string[] {
  const weekdays = [
    openingHours.sunday,
    openingHours.monday,
    openingHours.tuesday,
    openingHours.wednesday,
    openingHours.thursday,
    openingHours.friday,
    openingHours.saturday,
  ];

  const firstDayIndex = openingHours.startOfWeek ?? 1;

  return [
    ...weekdays.slice(firstDayIndex),
    ...weekdays.slice(0, firstDayIndex),
  ];
}

export function getOpeningHours(
  openingHoursData: OpenHours[],
  locale: string,
): string {
  const { openingHours } = navContentStore.state.dealer;
  locale =
    locale.split("-").length > 2
      ? locale.split("-").slice(0, 2).join("-")
      : locale;

  if (!openingHours) {
    return "";
  }

  // Get the arranged weekdays based on locale
  const arrangedDays = getArrangedWeekdays(openingHours);

  const openingHoursMap = getOpeningHoursMap(openingHours, openingHoursData);
  const result: string[] = [];
  let previousOpeningHours = "";
  let previousDayIndex = -1;
  let consecutiveDays: string[] = [];

  arrangedDays.forEach((day, index) => {
    const openHours = openingHoursMap.get(day);
    const openingHoursString = getOpeningHoursString(openHours, locale);

    if (openingHoursString !== "") {
      if (previousOpeningHours === "") {
        previousOpeningHours = openingHoursString;
        consecutiveDays.push(day);
      } else if (
        previousOpeningHours !== openingHoursString ||
        previousDayIndex !== index - 1
      ) {
        result.push(getDayRange(consecutiveDays, previousOpeningHours));
        consecutiveDays = [day];
        previousOpeningHours = openingHoursString;
      } else {
        consecutiveDays.push(day);
      }
      previousDayIndex = index;
    }
  });

  if (previousOpeningHours !== "") {
    result.push(getDayRange(consecutiveDays, previousOpeningHours));
  }

  return result.join("\n");
}

function getDayRange(days: string[], openingHoursString: string): string {
  if (days.length === 1) {
    return `${days[0]}: ${openingHoursString}`;
  }
  return `${days[0]} - ${days[days.length - 1]}: ${openingHoursString}`;
}

function getOpeningHoursMap(
  openingHours: OpeningHours,
  openingHoursData: OpenHours[],
): Map<string, { open: string; close: string }> {
  const openingHoursMap = new Map<string, { open: string; close: string }>();

  openingHoursData.forEach((item) => {
    const day = openingHours[item.day.toLowerCase() as keyof OpeningHours];

    if (isString(day)) {
      openingHoursMap.set(day, { open: item.open, close: item.close });
    }
  });

  return openingHoursMap;
}

function getOpeningHoursString(
  openHours: { open: string; close: string } | undefined,
  locale: string,
): string {
  return openHours && openHours.open !== "" && openHours.close !== ""
    ? getTimeFormat(openHours, locale)
    : "";
}

function getTimeFormat(
  openHours: { open: string; close: string },
  locale: string,
): string {
  const { openingHours } = navContentStore.state.dealer;

  const timeFormat = openingHours?.timeFormat === timeformat;

  return `${convertToLocalTime(openHours.open, locale, timeFormat)} - ${convertToLocalTime(openHours.close, locale, timeFormat)}`;
}

function convertToLocalTime(
  time: string,
  locale: string,
  timeFormat = false,
): string {
  const [hour, minute] = time.split(":").map(Number);
  const date = new Date(0, 0, 0, hour, minute);

  return date
    .toLocaleString(locale, {
      hour: "numeric",
      minute: "numeric",
      hour12: timeFormat,
    })
    .toUpperCase();
}
